// Advertencia «esta aseadora ya tiene otro aseo ese día» — CUÁNDO preguntar
// (round 2026-09-11). QUÉ cuenta como «mismo día» y «le toca» lo decide el
// backend (backend/src/utils/aseosMismoDia.js) y NO se recalcula acá. La UI vive
// en Pages/aseos/components/AvisoAseosMismoDia.tsx.
import dayjs from "dayjs";
import { hoyChile } from "./fechaChile";

/** Consulta a GET /api/aseos/mismo-dia. */
export interface ConsultaMismoDia {
  usuarioIds: number[];
  desde: string;
  hasta: string;
  excluirAseoId: number | null;
}

const FECHA_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Dayjs o "YYYY-MM-DD" → "YYYY-MM-DD"; cualquier otra cosa → null. */
function aFechaISO(v: unknown): string | null {
  if (typeof v === "string") return FECHA_RE.test(v) ? v : null;
  if (dayjs.isDayjs(v)) return v.isValid() ? v.format("YYYY-MM-DD") : null;
  return null;
}

/**
 * La consulta que corresponde al estado del form, o `null` si no hay nada que
 * preguntar: sin aseadoras, fechas incompletas o invertidas, o un aseo que ya
 * terminó (`hasta` antes de hoy). Un aseo pasado ya no se puede topar con nada,
 * y corregirle el pago no debe mostrar avisos de agenda.
 *
 * @param hoy "YYYY-MM-DD" de hoy en Chile (parámetro solo para los tests).
 */
export function consultaMismoDia(
  input: {
    usuarioIds: ReadonlyArray<unknown>;
    desde: unknown;
    hasta?: unknown;
    excluirAseoId?: unknown;
  },
  hoy: string = hoyChile(),
): ConsultaMismoDia | null {
  const usuarioIds: number[] = [];
  for (const raw of input.usuarioIds ?? []) {
    if (raw === null || raw === undefined || raw === "") continue;
    const id = Number(raw);
    if (Number.isInteger(id) && id > 0 && !usuarioIds.includes(id)) usuarioIds.push(id);
  }
  const desde = aFechaISO(input.desde);
  const hasta = aFechaISO(input.hasta) ?? desde;
  if (usuarioIds.length === 0 || !desde || !hasta || hasta < desde) return null;
  if (hasta < hoy) return null;
  const excluir = Number(input.excluirAseoId);
  return {
    usuarioIds,
    desde,
    hasta,
    excluirAseoId: Number.isInteger(excluir) && excluir > 0 ? excluir : null,
  };
}

/**
 * ¿Es la misma pregunta? Mismo equipo (sin importar el orden) y mismas fechas.
 * El aseo excluido no cuenta: es el mismo en las dos.
 */
export function mismaConsultaMismoDia(
  a: ConsultaMismoDia | null,
  b: ConsultaMismoDia | null,
): boolean {
  if (!a || !b) return false;
  const clave = (c: ConsultaMismoDia) =>
    `${[...c.usuarioIds].sort((x, y) => x - y).join(",")}|${c.desde}|${c.hasta}`;
  return clave(a) === clave(b);
}
